import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import useSaveFeature from "../../hooks/useSaveFeature";

import Alert from "../../components/alert/Alert";
import FeaturesFormSwitcher from "../../components/forms/FeaturesFormSwitcher";
import { FeatureEnum, toFeatureEnumOf } from "../../types/feature";
import useFeatures from "../../hooks/useFeatures";
import { getFeatureLabel } from "../../utils/features";

import ObjectCodeForm from "../../components/forms/ObjectCodeForm";
import useIsDesktop from "../../hooks/useIsDesktop";
import { Helmet } from "react-helmet-async";


const FeatureEdit = () => {
  const intl = useIntl();
  const params = useParams();
  const typeParam = params["type"];
  const [search] = useSearchParams();
  const idSearchParam = search.get('id');
  const objCodeSearchParam = search.get('objCode');
  const location = useLocation();
  const isEditMode = location.pathname.includes("edit");
  
  const [feature, setFeature] = useState<FeatureEnum|null>(null);
  const [form, updateForm] = useState<unknown>(null);
  const [globalError, setGlobalError] = useState<Error|null>(null);
  const [objCode, updateObjCode] = useState('');
  const [objName, setObjName] = useState('');
  const [objId, updateObjId] = useState<number|null>(null);

  const isDesktop = useIsDesktop();
  const inlineModeCodeForm = isDesktop;
  
  const {
    saveFeature: saveFeatureRequest,
    saveResponse,
    error
  } = useSaveFeature();

  const {
    data, error: errorFetchData, getFeatures, objName : objNameRequest
  } = useFeatures();

  
  /** init */
  useEffect(() => {
    const feature = toFeatureEnumOf(typeParam ?? ''); 
    setFeature(feature ?? null);
    if (isEditMode) {
      if (idSearchParam && !isNaN(+idSearchParam)) {
        getFeatures(+idSearchParam);
        updateObjId(+idSearchParam);
      }
      if (objCodeSearchParam) {
        updateObjCode(objCodeSearchParam);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, idSearchParam, objCodeSearchParam])


  useEffect(() => {
    if(objNameRequest){
      setObjName(objNameRequest);
    }
    if (data && data.length > 0) {
      const activeFeature = data.filter((feat) => feat.isActive);
      if (activeFeature.length > 0) {
        const formData = activeFeature[0].data;
        updateForm(formData);
      }
    } else {
      updateForm(null)
    }
  }, [data, objNameRequest])

  useEffect(() => {
    if (error) {
      setGlobalError(error);
    }
    if (errorFetchData) {
      setGlobalError(errorFetchData)
    }
  }, [error, errorFetchData]);

  function onFeaturesFormSwitcherChange(value: unknown) {   
    console.log(value)
    // return;
    if (value && feature) {
      updateForm(value);
      if (!objId) {
        const err = new Error(intl.formatMessage({ id: "features.validateObjectId" }))
        setGlobalError(err);
      } else {
        const actualFeatureType = toFeatureEnumOf(feature);
        if (actualFeatureType) {
          saveFeatureRequest(objId, actualFeatureType, value, objName);
        }
      }
    }
  }

  function handleFeatureChange(value: string) {
    const featureSelected = toFeatureEnumOf(value)!;
    const featureDataSelected = data?.find(d => d.type === featureSelected);
    setFeature(featureSelected);
    updateForm(featureDataSelected ? featureDataSelected.data : null);
  }

  const onChangeCode = useCallback((code: string) => updateObjCode(code), [])

  const onValidObject = useCallback((id: number) => updateObjId(id), [])
  
  console.log(feature)
  return (
    <>
      <Helmet>
        {
          isEditMode ?
            <title>
              {intl.formatMessage(
                { id: "features.editDocumentTitle" },
                { appName: import.meta.env.VITE_APP_NAME }
              )}
            </title> 
          :
            <title>
              {intl.formatMessage(
                { id: "features.addDocumentTitle" },
                { appName: import.meta.env.VITE_APP_NAME }
              )}
            </title>
        }
      </Helmet>

      <div className="py-10">

        {/* Change functionality */}
        { isEditMode &&
          <div className="mb-5 lg:w-1/2
            flex flex-col md:flex-row md:items-center gap-1 md:gap-5
            lg:flex-col xl:flex-row lg:items-start xl:items-center lg:gap-1 xl:gap-5">
            <label htmlFor="selectedFeature" className="text-shadow font-semibold text-md whitespace-nowrap">
              {intl.formatMessage({ id: "equipment.edit.changeFeature" })}
            </label>
            <select className="cursor-pointer input-sm" name="selectedFeature" id="selectedFeature"
              value={feature || undefined} onChange={(e) => handleFeatureChange(e.target.value)}>
              {
                Object.values(FeatureEnum).map(value => {
                  const title = getFeatureLabel(intl, value)!;
                  return (
                    <option className="cursor-pointer" key={value} value={value}>{title}</option>
                  )
                })
              }
            </select>
          </div>
        }

        {/* ID|CODE BADGE */}
        <ObjectCodeForm
          readonly={isEditMode}
          inlineMode={inlineModeCodeForm}
          onValid={onValidObject}
          objCode={objCode}           
          onChange={onChangeCode}
        />

        {/* Form switcher */}
        { feature &&
          <div className="lg:w-1/2">
            <FeaturesFormSwitcher
              data={form} featureType={feature} objName={objName}
              updateObjName={setObjName}
              valueFormChange={onFeaturesFormSwitcherChange}
            />
          </div>
        }

        {/* Response alert */}
        { saveResponse && (
          <Alert
            className="my-8 w-fit"
            message={intl.formatMessage({ id: "features.objectSaved" })}
            type="success"
            autoHidden={false}
          />
        )}
        { globalError && <Alert className="my-8 w-fit" message={globalError.message} type="error" autoHidden={false} /> }

      </div>
    </>
  )
}

export default FeatureEdit;
